import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {IntersectionObserver | undefined} */
  virtualScrollObserver;

  /** @type {number} */
  virtualPageSize = 0;

  /** @type {number} */
  virtualVisibleCount = 0;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Load all previous pages if starting from page > 1 (for "Show More" mode only)
    // Only do this if there's a "Show More" button present
    if (this.hasAttribute('disable-infinity-scroll') && this.refs.showMoreBtn) {
      this.#loadAllPreviousPages();
    }

    // Only initialize infinity scroll if not disabled
    if (!this.hasAttribute('disable-infinity-scroll')) {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // Initialize virtual pagination if enabled
    this.#initVirtualPagination();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    if (this.virtualScrollObserver) {
      this.virtualScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Initialize virtual pagination - shows items in batches as user scrolls
   */
  #initVirtualPagination() {
    const virtualPageSizeAttr = this.getAttribute('virtual-page-size');
    if (!virtualPageSizeAttr) return;

    this.virtualPageSize = parseInt(virtualPageSizeAttr, 10);
    if (isNaN(this.virtualPageSize) || this.virtualPageSize <= 0) return;

    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return;

    // Count initially visible cards (not hidden by Liquid)
    this.virtualVisibleCount = cards.filter(c => !c.hasAttribute('data-virtual-hidden')).length;

    this.#observeVirtualScroll();
  }

  /**
   * Apply visibility to cards based on virtual pagination
   */
  #applyVirtualVisibility() {
    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;

    cards.forEach((card, index) => {
      if (index < this.virtualVisibleCount) {
        card.style.display = '';
        card.removeAttribute('data-virtual-hidden');
      } else {
        card.style.display = 'none';
        card.setAttribute('data-virtual-hidden', 'true');
      }
    });
  }

  /**
   * Observe scroll to show more virtual items
   */
  #observeVirtualScroll() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;

    this.virtualScrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#showMoreVirtualItems();
          }
        }
      },
      {
        rootMargin: '200px',
      }
    );

    this.virtualScrollObserver.observe(viewMoreNext);
  }

  /**
   * Show more virtual items
   */
  #showMoreVirtualItems() {
    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;

    const totalCards = cards.length;
    
    // All cards already visible
    if (this.virtualVisibleCount >= totalCards) {
      // Check if we need to load more from server pagination
      if (!this.hasAttribute('disable-infinity-scroll')) {
        this.#renderNextPage();
      }
      return;
    }

    // Show next batch
    const newVisibleCount = Math.min(this.virtualVisibleCount + this.virtualPageSize, totalCards);
    
    for (let i = this.virtualVisibleCount; i < newVisibleCount; i++) {
      const card = cards[i];
      if (card) {
        card.style.display = '';
        card.removeAttribute('data-virtual-hidden');
      }
    }

    this.virtualVisibleCount = newVisibleCount;
    this.#aspectRatioHelper?.processNewElements();
  }

  /**
   * Reset virtual pagination (called after filter updates)
   */
  #resetVirtualPagination() {
    if (this.virtualPageSize <= 0) return;

    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return;

    this.virtualVisibleCount = cards.length;
    this.#applyVirtualVisibility();
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * Load all previous pages when starting from page > 1
   */
  async #loadAllPreviousPages() {
    const { cards, grid } = this.refs;
    
    if (!Array.isArray(cards) || cards.length === 0 || !grid) return;

    const currentPage = Number(cards[0].dataset.page);
    
    // If we're on page 1, nothing to load
    if (currentPage <= 1) return;

    // Load all pages from 1 to current page - 1
    const pagesToLoad = [];
    for (let page = 1; page < currentPage; page++) {
      pagesToLoad.push(page);
    }

    // Fetch all pages in parallel
    await Promise.all(
      pagesToLoad.map(async (page) => {
        const url = new URL(window.location.href);
        url.searchParams.set('page', page.toString());
        url.hash = '';
        await this.#fetchSpecificPage(page, url);
      })
    );

    // Now prepend all pages in order
    for (const page of pagesToLoad.reverse()) {
      const pageElements = this.#getGridForPage(page);
      if (pageElements && pageElements.length > 0) {
        grid.prepend(...pageElements);
      }
    }

    this.#aspectRatioHelper?.processNewElements();

    // Clean up URL - remove page parameter
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('page');
    history.replaceState('', '', cleanUrl.toString());
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage(updateHistory = true) {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    // Hide newly added elements if virtual pagination is enabled
    if (this.virtualPageSize > 0) {
      nextPageItemElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.style.display = 'none';
          element.setAttribute('data-virtual-hidden', 'true');
        }
      });
    }

    this.#aspectRatioHelper?.processNewElements();

    if (updateHistory) {
      history.pushState('', '', nextPage.url.toString());
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Public method to load the next page (for "Show More" button)
   */
  async loadNextPage() {
    await this.#renderNextPage(false);
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver((mutations) => {
      // Detect DOM update: either data-last-page changed or children were modified
      const hasChildListChange = mutations.some(m => m.type === 'childList');
      const newLastPage = this.refs.grid?.dataset.lastPage;
      const lastPageChanged = newLastPage !== currentLastPage;

      if (lastPageChanged || hasChildListChange) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Reset virtual pagination with new content
        this.#resetVirtualPagination();
        
        // Re-observe virtual scroll if needed
        if (this.virtualPageSize > 0 && this.virtualScrollObserver) {
          this.virtualScrollObserver.disconnect();
          this.#observeVirtualScroll();
        }

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
